import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { useSearchParams } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Loader2,
  AlertTriangle,
  Info,
  CheckCircle,
  XCircle,
  X,
  ArrowRight,
  Mail,
  Lock,
  ArrowLeft,
  User,
} from "lucide-react";

const LoginProfessional = ({ onLoginSuccess, onNavigateToClientFirstTime }) => {
  const [searchParams] = useSearchParams();
  const userRole = searchParams.get('role') || 'constructora'; // default a constructora
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isIPhone, setIsIPhone] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: login, 2: email primera vez, 3: crear password
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [focusedInput, setFocusedInput] = useState("");

  // ✅ NUEVO: Estado para mensaje personalizado
  const [loginMessage, setLoginMessage] = useState('');
  const [loginMessageType, setLoginMessageType] = useState('');

  // Estados para login
  const [loginData, setLoginData] = useState({ email: "", password: "" });

  // Estados para primera vez (unificado)
  const [primeraVezEmail, setPrimeraVezEmail] = useState("");
  const [primeraVezTipo, setPrimeraVezTipo] = useState(null); // 'cliente' | 'empleado' | null
  const [primeraVezPassword, setPrimeraVezPassword] = useState("");
  const [primeraVezConfirm, setPrimeraVezConfirm] = useState("");

  // Configuración anti-zoom y responsive
  useEffect(() => {
    if (typeof document !== "undefined") {
      let metaViewport = document.querySelector('meta[name="viewport"]');
      if (!metaViewport) {
        metaViewport = document.createElement("meta");
        metaViewport.name = "viewport";
        document.head.appendChild(metaViewport);
      }
      metaViewport.content =
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
    }

    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsMobile(width <= 768);
      setIsIPhone(width <= 393 && height >= 800);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => setCapsLockOn(e.getModifierState("CapsLock"));
    const handleKeyUp = (e) => setCapsLockOn(e.getModifierState("CapsLock"));
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // ✅ NUEVO: Leer mensaje personalizado del localStorage
  useEffect(() => {
    console.log('🔍 LoginProfessional: Leyendo mensaje del localStorage...');
    const message = localStorage.getItem('loginMessage');
    const messageType = localStorage.getItem('loginMessageType');
    
    console.log('📊 LoginProfessional: Mensaje encontrado:', message);
    console.log('📊 LoginProfessional: Tipo de mensaje:', messageType);
    
    if (message) {
      console.log('✅ LoginProfessional: Configurando mensaje:', message);
      setLoginMessage(message);
      setLoginMessageType(messageType || 'error');
      
      // Limpiar el mensaje del localStorage después de leerlo
      localStorage.removeItem('loginMessage');
      localStorage.removeItem('loginMessageType');
      console.log('🧹 LoginProfessional: Mensaje limpiado del localStorage');
      
      // ✅ NUEVO: Auto-limpiar mensaje después de 10 segundos
      const timer = setTimeout(() => {
        console.log('⏰ LoginProfessional: Auto-limpiando mensaje después de 10 segundos');
        setLoginMessage('');
        setLoginMessageType('');
      }, 10000);
      
      // Limpiar timer si el componente se desmonta
      return () => clearTimeout(timer);
    } else {
      console.log('ℹ️ LoginProfessional: No hay mensaje en localStorage');
    }
  }, []);

  // Función para mostrar alertas
  const showAlert = (type, title, message) => {
    const id = Date.now();
    setAlerts((prev) => [...prev, { id, type, title, message }]);
    setTimeout(
      () => setAlerts((prev) => prev.filter((a) => a.id !== id)),
      5000
    );
  };

  const closeAlert = (id) =>
    setAlerts((prev) => prev.filter((a) => a.id !== id));

  // LOGIN
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(
        "https://nokk-backend.vercel.app/api/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(loginData),
        }
      );
      const result = await response.json();
      if (!result.success) {
        showAlert(
          "error",
          "Credenciales Incorrectas",
          result.error || "Email o contraseña incorrectos"
        );
        setLoading(false);
        return;
      }
      showAlert("success", "¡Bienvenido!", "Iniciando sesión...");
      setTimeout(() => onLoginSuccess(result.data), 1000);
    } catch (err) {
      showAlert("error", "Error de Conexión", "Error en login: " + err.message);
    }
    setLoading(false);
  };

  // Handler para validar email de primera vez
  const handlePrimeraVezBuscar = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPrimeraVezTipo(null);
    setPrimeraVezPassword("");
    setPrimeraVezConfirm("");
    try {
      const response = await fetch(
        "https://nokk-backend.vercel.app/api/usuario-existe",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: primeraVezEmail }),
        }
      );
      const result = await response.json();
      if (result.success && result.usuario && !result.usuario.password) {
        const tipo = result.usuario.tipo_usuario === "empleado" ? "empleado" : "cliente";
        setPrimeraVezTipo(tipo);
        setCurrentStep(3);
      } else if (result.success && result.usuario && result.usuario.password) {
        showAlert(
          "info",
          "Usuario Existente",
          "Ya tienes una contraseña creada. Usa el login normal."
        );
        setTimeout(() => {
          setCurrentStep(1);
          setPrimeraVezEmail("");
        }, 2000);
      } else {
        showAlert(
          "warning",
          "Usuario No Encontrado",
          "Este email no está registrado."
        );
      }
    } catch (err) {
      showAlert("error", "Error de Búsqueda", "Error: " + err.message);
    }
    setLoading(false);
  };

  // Handler para crear contraseña (unificado)
  const handlePrimeraVezCrearPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!primeraVezPassword || primeraVezPassword.length < 6) {
      showAlert(
        "warning",
        "Contraseña Inválida",
        "La contraseña debe tener al menos 6 caracteres"
      );
      setLoading(false);
      return;
    }
    if (primeraVezPassword !== primeraVezConfirm) {
      showAlert("warning", "No coinciden", "Las contraseñas no coinciden.");
      setLoading(false);
      return;
    }
    try {
      const endpoint =
        primeraVezTipo === "empleado"
          ? "https://nokk-backend.vercel.app/api/primer-login-empleado"
          : "https://nokk-backend.vercel.app/api/primer-login-cliente";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: primeraVezEmail,
          password: primeraVezPassword,
        }),
      });
      const result = await response.json();
      if (result.success) {
        showAlert(
          "success",
          "¡Contraseña Creada!",
          "Contraseña creada exitosamente. ¡Bienvenido/a!"
        );
        setTimeout(() => {
          setCurrentStep(1);
          setPrimeraVezEmail("");
          setPrimeraVezPassword("");
          setPrimeraVezConfirm("");
          setPrimeraVezTipo(null);
        }, 1500);
      } else {
        showAlert(
          "error",
          "Error",
          result.error || "Error al crear contraseña."
        );
      }
    } catch (err) {
      showAlert("error", "Error al Crear", "Error: " + err.message);
    }
    setLoading(false);
  };

  // Navegación entre pasos
  const handleNext = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep === 3) {
      setCurrentStep(2);
      setPrimeraVezPassword("");
      setPrimeraVezConfirm("");
    } else if (currentStep === 2) {
      setCurrentStep(1);
      setPrimeraVezEmail("");
    }
  };

  // Obtener título y subtítulo según el paso
  const getStepInfo = () => {
    const isCliente = userRole === 'cliente';
    
    switch (currentStep) {
      case 1:
        return {
          title: isCliente ? "Accedé a tu proyecto" : "Accedé a tu constructora",
          subtitle: loginMessage || (isCliente 
            ? "Inicia sesión para ver el progreso de tu obra" 
            : "Inicia sesión para gestionar tus proyectos"),
        };
      case 2:
        return {
          title: "Primera vez",
          subtitle: "(Creá tu contraseña)",
        };
      case 3:
        return {
          title: "Crear contraseña",
          subtitle: `Configura tu acceso como ${
            primeraVezTipo === "empleado" ? "empleado" : "cliente"
          }`,
        };
      default:
        return {
          title: isCliente ? "Accedé a tu proyecto" : "Accedé a tu constructora",
          subtitle: loginMessage || (isCliente 
            ? "Inicia sesión para ver el progreso de tu obra" 
            : "Inicia sesión para gestionar tus proyectos"),
        };
    }
  };

  // Render del paso 1 - Login
  const renderStep1 = () => (
    <form
      onSubmit={handleLogin}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: isIPhone ? "18px" : isMobile ? "20px" : "22px",
        height: "100%",
      }}
    >
      {/* Campo Email */}
      <div>
        <label
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: isIPhone ? "13px" : "14px",
            fontWeight: "600",
            color: "#374151",
          }}
        >
          Email
        </label>
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          >
            <Mail size={18} color="#64748b" />
          </div>
          <input
            type="email"
            value={loginData.email}
            onChange={(e) =>
              setLoginData({ ...loginData, email: e.target.value })
            }
            placeholder="tu@constructora.com"
            required
            autoComplete="email"
            style={{
              width: "100%",
              padding: isIPhone ? "14px 16px 14px 52px" : "16px 20px 16px 56px",
              border: "2px solid #f1f5f9",
              borderRadius: "12px",
              fontSize: "16px",
              boxSizing: "border-box",
              outline: "none",
              background: "#fafafa",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              fontFamily: "inherit",
              touchAction: "manipulation",
              WebkitAppearance: "none",
              appearance: "none",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#3b82f6";
              e.target.style.background = "white";
              e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#f1f5f9";
              e.target.style.background = "#fafafa";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>
      </div>

      {/* Campo Contraseña */}
      <div>
        <label
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: isIPhone ? "13px" : "14px",
            fontWeight: "600",
            color: "#374151",
          }}
        >
          Contraseña
        </label>
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          >
            <Lock size={18} color="#64748b" />
          </div>
          <input
            type={showPassword ? "text" : "password"}
            value={loginData.password}
            onChange={(e) =>
              setLoginData({ ...loginData, password: e.target.value })
            }
            placeholder="Ingresa tu contraseña"
            required
            autoComplete="current-password"
            style={{
              width: "100%",
              padding: isIPhone ? "14px 52px 14px 52px" : "16px 56px 16px 56px",
              border: "2px solid #f1f5f9",
              borderRadius: "12px",
              fontSize: "16px",
              boxSizing: "border-box",
              outline: "none",
              background: "#fafafa",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              fontFamily: "inherit",
              touchAction: "manipulation",
              WebkitAppearance: "none",
              appearance: "none",
            }}
            onFocus={(e) => {
              setFocusedInput("login-password");
              e.target.style.borderColor = "#3b82f6";
              e.target.style.background = "white";
              e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
            }}
            onBlur={(e) => {
              setFocusedInput("");
              e.target.style.borderColor = "#f1f5f9";
              e.target.style.background = "#fafafa";
              e.target.style.boxShadow = "none";
            }}
          />
          <div
            style={{
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {capsLockOn && focusedInput === "login-password" && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#ef4444",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                ⇪
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#64748b",
                padding: "6px",
                display: "flex",
                alignItems: "center",
                borderRadius: "6px",
                transition: "all 0.2s ease",
                touchAction: "manipulation",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f1f5f9";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
              }}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Botón de login */}
      <button
        type="submit"
        disabled={loading}
        className="button-hover"
        style={{
          width: "100%",
          padding: "16px",
          background: loading
            ? "#94a3b8"
            : "linear-gradient(135deg, #1a1a1a 0%, #374151 100%)",
          color: "white",
          border: "none",
          borderRadius: "12px",
          fontSize: "16px",
          fontWeight: "600",
          cursor: loading ? "not-allowed" : "pointer",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: loading ? "none" : "0 4px 16px rgba(26, 26, 26, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          touchAction: "manipulation",
          marginTop: "8px",
        }}
      >
        {loading && <Loader2 size={18} className="animate-spin" />}
        {loading ? "Iniciando sesión..." : "Iniciar sesión"}
        {!loading && <ArrowRight size={18} />}
      </button>

      {/* Divider */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          margin: "12px 0 8px 0",
        }}
      >
        <div
          style={{
            flex: 1,
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, #e2e8f0, transparent)",
          }}
        />
        <span
          style={{
            fontSize: "12px",
            color: "#64748b",
            fontWeight: "500",
          }}
        >
          o
        </span>
        <div
          style={{
            flex: 1,
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, #e2e8f0, transparent)",
          }}
        />
      </div>

      {/* Botón Primera vez */}
      <button
        type="button"
        onClick={handleNext}
        style={{
          background: "transparent",
          color: "#3b82f6",
          border: "none",
          fontSize: "15px",
          fontWeight: "600",
          cursor: "pointer",
          textDecoration: "none",
          transition: "all 0.2s ease",
          fontFamily: "inherit",
          padding: "8px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          touchAction: "manipulation",
        }}
        onMouseEnter={(e) => {
          e.target.style.color = "#1d4ed8";
          e.target.style.textDecoration = "underline";
        }}
        onMouseLeave={(e) => {
          e.target.style.color = "#3b82f6";
          e.target.style.textDecoration = "none";
        }}
      >
        Primera vez
      </button>
      <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, textAlign: 'center' }}>(Creá tu contraseña)</div>
    </form>
  );

  // Render del paso 2 - Email primera vez
  const renderStep2 = () => (
    <form
      onSubmit={handlePrimeraVezBuscar}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: isIPhone ? "20px" : "24px",
        height: "100%",
        justifyContent: "center",
      }}
    >
      <div>
        <label
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: isIPhone ? "13px" : "14px",
            fontWeight: "600",
            color: "#374151",
          }}
        >
          Email
        </label>
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          >
            <Mail size={18} color="#64748b" />
          </div>
          <input
            type="email"
            value={primeraVezEmail}
            onChange={(e) => setPrimeraVezEmail(e.target.value)}
            placeholder="tu@email.com"
            required
            autoComplete="email"
            style={{
              width: "100%",
              padding: isIPhone ? "14px 16px 14px 52px" : "16px 20px 16px 56px",
              border: "2px solid #f1f5f9",
              borderRadius: "12px",
              fontSize: "16px",
              boxSizing: "border-box",
              outline: "none",
              background: "#fafafa",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              fontFamily: "inherit",
              touchAction: "manipulation",
              WebkitAppearance: "none",
              appearance: "none",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#3b82f6";
              e.target.style.background = "white";
              e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#f1f5f9";
              e.target.style.background = "#fafafa";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="button-hover"
        style={{
          width: "100%",
          padding: "16px",
          background: loading
            ? "#94a3b8"
            : "linear-gradient(135deg, #1a1a1a 0%, #374151 100%)",
          color: "white",
          border: "none",
          borderRadius: "12px",
          fontSize: "16px",
          fontWeight: "600",
          cursor: loading ? "not-allowed" : "pointer",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: loading ? "none" : "0 4px 16px rgba(26, 26, 26, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          touchAction: "manipulation",
        }}
      >
        {loading && <Loader2 size={18} className="animate-spin" />}
        {loading ? "Validando..." : "Validar email"}
      </button>

      <button
        type="button"
        onClick={handleBack}
        style={{
          background: "transparent",
          color: "#64748b",
          border: "none",
          fontSize: "14px",
          fontWeight: "600",
          cursor: "pointer",
          padding: "8px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          touchAction: "manipulation",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.target.style.color = "#374151";
        }}
        onMouseLeave={(e) => {
          e.target.style.color = "#64748b";
        }}
      >
        <ArrowLeft size={16} />
        Volver al login
      </button>
    </form>
  );

  // Render del paso 3 - Crear contraseña
  const renderStep3 = () => (
    <form
      onSubmit={handlePrimeraVezCrearPassword}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: isIPhone ? "16px" : "20px",
        height: "100%",
      }}
    >
      {/* Indicador de tipo validado */}
      <div
        style={{
          textAlign: "center",
          padding: "12px 16px",
          background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
          border: "1px solid #a7f3d0",
          borderRadius: "12px",
          marginBottom: "8px",
        }}
      >
        <div
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "#059669",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <CheckCircle size={16} />
          {primeraVezTipo === "empleado"
            ? "Empleado validado"
            : "Cliente validado"}
        </div>
        <div
          style={{
            fontSize: "12px",
            color: "#047857",
            marginTop: "4px",
          }}
        >
          {primeraVezEmail}
        </div>
      </div>

      {/* Campo Nueva Contraseña */}
      <div>
        <label
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: isIPhone ? "13px" : "14px",
            fontWeight: "600",
            color: "#374151",
          }}
        >
          Nueva contraseña
        </label>
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          >
            <Lock size={18} color="#64748b" />
          </div>
          <input
            type={showPassword ? "text" : "password"}
            value={primeraVezPassword}
            onChange={(e) => setPrimeraVezPassword(e.target.value)}
            placeholder="Mínimo 6 caracteres"
            required
            autoComplete="new-password"
            style={{
              width: "100%",
              padding: isIPhone ? "14px 52px 14px 52px" : "16px 56px 16px 56px",
              border: "2px solid #f1f5f9",
              borderRadius: "12px",
              fontSize: "16px",
              boxSizing: "border-box",
              outline: "none",
              background: "#fafafa",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              fontFamily: "inherit",
              touchAction: "manipulation",
              WebkitAppearance: "none",
              appearance: "none",
            }}
            onFocus={(e) => {
              setFocusedInput("primera-vez-password");
              e.target.style.borderColor = "#3b82f6";
              e.target.style.background = "white";
              e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
            }}
            onBlur={(e) => {
              setFocusedInput("");
              e.target.style.borderColor = "#f1f5f9";
              e.target.style.background = "#fafafa";
              e.target.style.boxShadow = "none";
            }}
          />
          <div
            style={{
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {capsLockOn && focusedInput === "primera-vez-password" && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#ef4444",
                  fontWeight: "600",
                }}
              >
                ⇪
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#64748b",
                padding: "6px",
                display: "flex",
                alignItems: "center",
                borderRadius: "6px",
                transition: "all 0.2s ease",
                touchAction: "manipulation",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f1f5f9";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
              }}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Campo Confirmar Contraseña */}
      <div>
        <label
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: isIPhone ? "13px" : "14px",
            fontWeight: "600",
            color: "#374151",
          }}
        >
          Confirmar contraseña
        </label>
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          >
            <Lock size={18} color="#64748b" />
          </div>
          <input
            type={showPassword ? "text" : "password"}
            value={primeraVezConfirm}
            onChange={(e) => setPrimeraVezConfirm(e.target.value)}
            placeholder="Repite la contraseña"
            required
            autoComplete="new-password"
            style={{
              width: "100%",
              padding: isIPhone ? "14px 52px 14px 52px" : "16px 56px 16px 56px",
              border: "2px solid #f1f5f9",
              borderRadius: "12px",
              fontSize: "16px",
              boxSizing: "border-box",
              outline: "none",
              background: "#fafafa",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              fontFamily: "inherit",
              touchAction: "manipulation",
              WebkitAppearance: "none",
              appearance: "none",
            }}
            onFocus={(e) => {
              setFocusedInput("primera-vez-confirm");
              e.target.style.borderColor = "#3b82f6";
              e.target.style.background = "white";
              e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
            }}
            onBlur={(e) => {
              setFocusedInput("");
              e.target.style.borderColor = "#f1f5f9";
              e.target.style.background = "#fafafa";
              e.target.style.boxShadow = "none";
            }}
          />
          <div
            style={{
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {capsLockOn && focusedInput === "primera-vez-confirm" && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#ef4444",
                  fontWeight: "600",
                }}
              >
                ⇪
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#64748b",
                padding: "6px",
                display: "flex",
                alignItems: "center",
                borderRadius: "6px",
                transition: "all 0.2s ease",
                touchAction: "manipulation",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f1f5f9";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
              }}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Botón Crear Contraseña */}
      <button
        type="submit"
        disabled={loading}
        className="button-hover"
        style={{
          width: "100%",
          padding: "16px",
          background: loading
            ? "#94a3b8"
            : "linear-gradient(135deg, #1a1a1a 0%, #374151 100%)",
          color: "white",
          border: "none",
          borderRadius: "12px",
          fontSize: "16px",
          fontWeight: "600",
          cursor: loading ? "not-allowed" : "pointer",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: loading ? "none" : "0 4px 16px rgba(26, 26, 26, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          touchAction: "manipulation",
          marginTop: "8px",
        }}
      >
        {loading && <Loader2 size={18} className="animate-spin" />}
        {loading ? "Creando contraseña..." : "Crear contraseña"}
      </button>

      {/* Botón Volver */}
      <button
        type="button"
        onClick={handleBack}
        style={{
          background: "transparent",
          color: "#64748b",
          border: "none",
          fontSize: "14px",
          fontWeight: "600",
          cursor: "pointer",
          padding: "8px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          touchAction: "manipulation",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.target.style.color = "#374151";
        }}
        onMouseLeave={(e) => {
          e.target.style.color = "#64748b";
        }}
      >
        <ArrowLeft size={16} />
        Volver
      </button>
    </form>
  );

  const stepInfo = getStepInfo();

  return (
    <>
      {/* Estilos CSS globales */}
      <style jsx global>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        input[type="password"],
        textarea,
        select,
        button {
          font-size: 16px !important;
          -webkit-text-size-adjust: none;
          -webkit-appearance: none !important;
          appearance: none !important;
          -webkit-border-radius: 0;
          touch-action: manipulation;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        body {
          -webkit-text-size-adjust: 100%;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
          touch-action: manipulation;
        }

        /* Animaciones */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        .fade-in-up {
          animation: fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Soporte para dispositivos iOS */
        @supports (-webkit-touch-callout: none) {
          .login-container {
            min-height: -webkit-fill-available;
            padding-top: env(safe-area-inset-top);
            padding-bottom: env(safe-area-inset-bottom);
          }
        }

        /* Hover effects para desktop */
        @media (hover: hover) and (pointer: fine) {
          .button-hover:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(26, 26, 26, 0.25) !important;
          }
        }

        /* Mobile touch feedback */
        @media (max-width: 768px) {
          .button-hover:active {
            transform: scale(0.98);
          }
        }
      `}</style>

      {/* Sistema de alertas */}
      {alerts.map((alert) => (
        <div
          key={alert.id}
          style={{
            position: "fixed",
            top: "24px",
            right: "24px",
            zIndex: 9999,
            background:
              alert.type === "success"
                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                : alert.type === "error"
                ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                : alert.type === "warning"
                ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                : "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
            color: "white",
            padding: "16px 20px",
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            minWidth: isMobile ? "280px" : "320px",
            maxWidth: isMobile ? "calc(100vw - 48px)" : "420px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontWeight: "500",
            animation: "slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            backdropFilter: "blur(10px)",
          }}
        >
          <div style={{ flexShrink: 0 }}>
            {alert.type === "success" && <CheckCircle size={20} />}
            {alert.type === "error" && <XCircle size={20} />}
            {alert.type === "warning" && <AlertTriangle size={20} />}
            {alert.type === "info" && <Info size={20} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: "600", marginBottom: "2px" }}>
              {alert.title}
            </div>
            <div style={{ fontWeight: "400", opacity: 0.9, fontSize: "14px" }}>
              {alert.message}
            </div>
          </div>
          <button
            onClick={() => closeAlert(alert.id)}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "white",
              borderRadius: "8px",
              width: "32px",
              height: "32px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "all 0.2s ease",
              touchAction: "manipulation",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255,255,255,0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(255,255,255,0.2)";
            }}
          >
            <X size={16} />
          </button>
        </div>
      ))}

      {/* Container principal */}
      <div
        className="login-container"
        style={{
          minHeight: "100vh",
          minHeight: "100dvh",
          background: isMobile
            ? "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)"
            : "linear-gradient(135deg, #fafbfc 0%, #f1f5f9 25%, #e2e8f0 50%, #cbd5e1 75%, #94a3b8 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? "20px 16px" : "20px 24px",
          position: "relative",
          overflow: "hidden",
          fontFamily: '"Inter", sans-serif',
        }}
      >
        {/* Efectos de fondo */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isMobile
              ? `
                radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 70% 80%, rgba(16, 185, 129, 0.05) 0%, transparent 50%)
              `
              : `
                radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)
              `,
            pointerEvents: "none",
          }}
        />

        {/* Card principal FIJO e INAMOVIBLE - igual que el registro */}
        <div
          className="fade-in-up"
          style={{
            background: isMobile
              ? "rgba(255, 255, 255, 0.98)"
              : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            padding: isIPhone
              ? "24px 20px"
              : isMobile
              ? "32px 24px"
              : "40px 36px",
            width: "100%",
            maxWidth: isMobile ? "400px" : "450px",
            // DIMENSIONES FIJAS INAMOVIBLES
            height: isIPhone ? "500px" : isMobile ? "520px" : "540px",
            minHeight: isIPhone ? "500px" : isMobile ? "520px" : "540px",
            maxHeight: isIPhone ? "500px" : isMobile ? "520px" : "540px",
            boxShadow: isMobile
              ? "0 20px 60px -12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1)"
              : "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)",
            position: "relative",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header FIJO - Logo y título nunca se mueven */}
          <div
            style={{
              textAlign: "center",
              marginBottom: isIPhone ? "20px" : isMobile ? "24px" : "28px",
              flexShrink: 0,
            }}
          >
            {/* Logo FIJO */}
            <div
              style={{
                marginBottom: isIPhone ? "16px" : isMobile ? "20px" : "24px",
              }}
            >
              <img
                src="https://llohruvkcdcyolmwlivx.supabase.co/storage/v1/object/public/public-assets//logonokk.png"
                alt="NOKK"
                style={{
                  height: isIPhone ? "32px" : isMobile ? "36px" : "40px",
                  width: "auto",
                  opacity: 0.92,
                  cursor: "pointer",
                }}
                onClick={() => (window.location.href = "/")}
              />
            </div>

            {/* Título y subtítulo FIJOS - altura fija para que no salten */}
            <div
              style={{
                minHeight: isIPhone ? "64px" : isMobile ? "70px" : "76px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <h1
                style={{
                  fontSize: isIPhone ? "20px" : isMobile ? "22px" : "24px",
                  fontWeight: "800",
                  color: "#0f172a",
                  margin: "0 0 8px 0",
                  letterSpacing: "-0.02em",
                  lineHeight: "1.2",
                }}
              >
                {stepInfo.title}
              </h1>
              <p
                style={{
                  fontSize: isIPhone ? "13px" : isMobile ? "14px" : "15px",
                  color: loginMessage ? "#ef4444" : "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  lineHeight: "1.4",
                  fontStyle: loginMessage ? "italic" : "normal",
                }}
              >
                {stepInfo.subtitle}
              </p>
            </div>
          </div>

          {/* Contenedor de pasos con animación de slide horizontal */}
          <div
            style={{
              flex: 1,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                width: "300%", // 3 pasos = 300%
                transform: `translateX(-${(currentStep - 1) * 33.333}%)`,
                transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                height: "100%",
              }}
            >
              {/* Paso 1 - Login */}
              <div
                style={{
                  width: "33.333%",
                  paddingRight: isMobile ? "12px" : "16px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                {renderStep1()}
              </div>

              {/* Paso 2 - Email primera vez */}
              <div
                style={{
                  width: "33.333%",
                  paddingLeft: isMobile ? "12px" : "16px",
                  paddingRight: isMobile ? "12px" : "16px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                {renderStep2()}
              </div>

              {/* Paso 3 - Crear contraseña */}
              <div
                style={{
                  width: "33.333%",
                  paddingLeft: isMobile ? "12px" : "16px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  paddingTop: isIPhone ? "10px" : isMobile ? "12px" : "16px",
                }}
              >
                {renderStep3()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginProfessional;
